import { Router, ScreenBase } from "@frui.ts/screens";
import LocalizationService from "../../services/localizationService";
import { action, computed, observable, reaction, runInAction, toJS } from "mobx";
import AppRouter from "services/appRouter";
import SummaryViewModel from "./summaryMultiEshopViewModel";
import Enum from "../../models/enum";
import EnumService from "../../services/enumService";
import ChargeRegistrationService from "../../services/chargeRegistrationService";
import EventBus, { vehicleChargeOrderInstructions, vehicleChargeValidityInstructions } from "../../services/eventBus";
import { getStatusSteps } from "./multiEshopStatusSteps";
import BatchOrderViewModel from "./batchOrderViewModel";
import ChargeOrderService from "services/chargeOrderService";
import ChargeOrderItem from "models/chargeOrderItem";
import VehicleService from "services/vehicleService";
import ChargeValidityInstructionGroupsResponse from "entities/chargeValidityInstructionGroupsResponse";
import ChargeOrderInstructionGroupsResponse from "entities/chargeOrderInstructionGroupsResponse";
import FuelTypeResponse from "entities/fuelTypeResponse";
import ChargeValidityInstructionGroupResponse from "entities/chargeValidityInstructionGroupResponse";
import ChargeOverlapType from "entities/chargeOverlapType";
import ChargeOrderInstructionType from "entities/chargeOrderInstructionType";
import ChargeVehicleWarningReasonOrder from "entities/chargeVehicleWarningReasonOrder";
import ChargeTypesService from "services/chargeTypesService";
import ExemptionReasonResponse from "entities/exemptionReasonResponse";
import { isMobile } from "react-device-detect";
import { scrollToElement } from "utils/scrollTo";
import ChargeValidityOverlapInstructionsGroupResponse from "entities/chargeValidityOverlapInstructionsGroupResponse";
import { IVehicle } from "models/types";
import FuelTypeLimitationResponse from "entities/fuelTypeLimitationResponse";
import FuelTypeLimitationReasonOrder from "entities/fuelTypeLimitationReasonOrder";
import FuelTypeLimitationReason from "entities/fuelTypeLimitationReason";
import TimeService from "services/timeService";
import { dateToIsoDate, endOfCzechDay, isSamePragueDay, utcToCZZonedTime, zonedCZTimeToUtc } from "utils/dateUtils";
import { endOfDay } from "date-fns";
import { dateStringWithServerZone } from "../../utils/dateUtils";

export const OrderRecapViewModelNavigationName = "recap";

export enum OverlayValidity {
  VALID = "VALID",
}

class ChargeOverlay {
  licensePlate: string;
  state: OverlayValidity;
  expiration: Date;
  selected? = false;
}
class ChargeExemptions {
  instructionType: string;
  possibleExemptionId?: string;
  possibleExemptionReason?: string;
  vehicles?: IVehicle[];
}

class ChargePossibleExemptions {
  instructionType: string;
  possibleExemptionId?: string;
  fuelTypeLimitation: FuelTypeLimitationResponse;
  possibleExemptionReason?: string;
  fuelTypeLable: string;
  vehicles?: IVehicle[];
}
class CountryData {
  [key: string]: IVehicle[];
}

@Router.registerRoute({ name: "recap", route: OrderRecapViewModelNavigationName })
export default class OrderRecapViewModel extends ScreenBase {
  private unsubscription: Array<() => void> = [];

  @observable detail?: ChargeOrderItem = undefined;
  @observable detailIndex = -1;
  @observable loading: boolean;
  @observable summaryViewModel: SummaryViewModel;
  @observable possibleExemptionIds: string[] = [];
  @observable overlayRecommendationHidden = false;
  @observable overlayExemptionRecommendationHidden = false;

  @observable exemptions: ChargeExemptions[] = [];
  @observable exemptionsIdentifiedPossible: ChargeExemptions[] = [];
  @observable possibleExemptions: ChargePossibleExemptions[] = [];
  @observable overlays: ChargeOverlay[] = [];
  @observable overlapsExemption: ChargeValidityOverlapInstructionsGroupResponse[] = [];
  @observable overlaysTitle: string;
  @observable allOverlaysSelected: boolean | null = false;

  statusNavigation = getStatusSteps();

  protected subscriptions: Array<() => void> = [];

  constructor(
    public localizationService: LocalizationService,
    public chargeRegistrationService: ChargeRegistrationService,
    public timeService: TimeService,
    public vehicleService: VehicleService,
    private chargeOrderService: ChargeOrderService,
    private chargeTypesService: ChargeTypesService,
    private summaryViewModelFactory: ReturnType<typeof SummaryViewModel.Factory>,
    private enumService: EnumService,
    private router: AppRouter,
    protected eventBus: EventBus
  ) {
    super();
    this.navigationName = OrderRecapViewModelNavigationName;
    this.subscriptions.push(this.eventBus.subscribe(vehicleChargeOrderInstructions, this.vehicleChargeOrderInstructions));
    this.subscriptions.push(this.eventBus.subscribe(vehicleChargeValidityInstructions, this.vehicleChargeValidityInstructions));
  }

  protected async onInitialize(): Promise<any> {
    await this.enumService.lazyValues("countries");
    this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
    this.loading = true;
    this.chargeOrderService.chargeOrder.charges.forEach(ch => (ch._isModified = true));
    this.summaryViewModel = this.summaryViewModelFactory(false, this.loading, this.onContinue, this.onBack);
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.subscriptions.forEach(unsubscribe => unsubscribe());
      this.subscriptions = [];
    }

    return super.onDeactivate(isClosing);
  }

  protected async onActivate(): Promise<any> {
    if (isMobile && this.stepper) {
      scrollToElement(this.stepper);
    }
    this.loading = true;
    this.unsubscription.push(reaction(() => this.loading, this.onLoadingChange));
    const isChargesModified = this.chargeOrderService.chargeOrder.charges.some(charge => charge._isModified === true);
    if (isChargesModified) {
      this.resetObservables();
    }

    await this.loadVehicleInformation();

    return super.onActivate();
  }

  private async goBackIfNeeded() {
    if (toJS(this.chargeOrderService.chargeOrder.charges).flatMap(item => item.licensePlates).length === 0)
      await this.router.navigate(BatchOrderViewModel);
  }

  @computed
  get stepper() {
    return window.document.getElementById("stepper");
  }

  @computed
  get emptyState() {
    return this.localizationService.translateGeneral("eshop.order.summary.empty");
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  @computed
  get chargeOrderCharges() {
    return this.chargeOrderService.chargeOrder.charges;
  }

  @computed
  get translateVehicleCount() {
    return this.chargeOrderService.translateVehicleCount;
  }

  @computed
  get translateChargeLabel() {
    return this.chargeOrderService.chargeLabel;
  }

  @computed
  get exemptionRecommendationVisible() {
    return this.exemptionLicensePlates.length > 0;
  }

  @computed
  get possibleExemptionVisible() {
    return this.possibleExemption.length > 0;
  }

  @computed
  get overlayExemptionVisible() {
    return this.overlapsExemption?.length > 0 && !this.overlayExemptionRecommendationHidden;
  }

  @computed
  get overlayVisible() {
    return this.overlays.length > 0;
  }

  @computed
  get exemptionLicensePlates(): ChargeExemptions[] {
    return this.exemptions;
  }

  @computed
  get exemptionIdentifiedPossibleLicensePlate(): ChargeExemptions[] {
    return this.exemptionsIdentifiedPossible;
  }

  @computed
  get possibleExemption() {
    return this.possibleExemptions;
  }

  @computed
  get overlaysRemoveDisabled() {
    return !(this.overlays.filter(overlay => overlay.selected !== null && overlay.selected).length > 0);
  }

  @computed
  get countRecommendation() {
    return (
      (this.overlayRecommendationVisible ? 1 : 0) +
      (this.exemptionRecommendationVisible ? 1 : 0) +
      (this.overlayExemptionVisible ? 1 : 0) +
      (this.possibleExemptionVisible ? 1 : 0)
    );
  }

  @computed
  get overlayRecommendationVisible() {
    return this.overlayLicensePlates.length > 0 && !this.overlayRecommendationHidden;
  }

  @computed
  get overlayLicensePlates(): ChargeOverlay[] {
    return this.overlays;
  }

  @action.bound
  onLoadingChange() {
    this.summaryViewModel = this.summaryViewModelFactory(false, this.loading, this.onContinue, this.onBack);
  }

  @action.bound
  resetObservables() {
    this.possibleExemptionIds = [];
    this.overlayRecommendationHidden = false;
    this.overlayExemptionRecommendationHidden = false;
    this.allOverlaysSelected = false;
    this.overlays = [];
    this.exemptions = [];
    this.exemptionsIdentifiedPossible = [];
    this.overlapsExemption = [];
    this.possibleExemptions = [];
    this.chargeOrderService.chargeOrder.charges.forEach(ch => (ch._allowedDelete = false));
  }

  @action.bound
  getMappedExemptions(instructions: any): ChargeExemptions[] {
    return Object.keys(instructions).flatMap(key => {
      if (key === ChargeOrderInstructionType.IdentifiedPossibleExemption) {
        return instructions[key].map((i: any) => {
          return {
            instructionType: key,
            possibleExemptionId: Math.random().toString(),
            possibleExemptionReason: this.exemptionReasonWarning(i.possibleExemptionReasonIds),
            vehicles: i.vehicles.map((v: IVehicle) => v),
          };
        });
      }
      return {
        instructionType: key,
        possibleExemptionId: Math.random().toString(),
        vehicles: instructions[key].flatMap((i: any) => i.vehicles.flatMap((v: IVehicle) => v)),
      };
    });
  }

  @action.bound
  translateFuelTypeLimitationLink(code: string) {
    if (code === FuelTypeLimitationReason.DerivedFromVehicleRegisterData) {
      return {
        text: this.translateWithLink("possibleExemptions", "REGISTER_LINK"),
        link: this.translateWithLink("possibleExemptions", "REGISTER_LINK_URL"),
      };
    }
    return {
      text: this.translateWithLink("possibleExemptions", "ECO_LINK"),
      link: this.translateWithLink("possibleExemptions", "ECO_LINK_URL"),
    };
  }

  @action.bound
  translateFuelTypeLimitation(code: string) {
    return this.translateWithLink("possibleExemptions", code);
  }

  @action.bound
  translateWithLink(code: string, secondCode?: string) {
    return this.localizationService.translateGeneral(
      secondCode ? `multiEshop.${code}.${secondCode}` : `eshop.order.${this.navigationName}.${code}`,
      undefined,
      true // Important for markdown links to function correctly
    );
  }
  @action.bound
  getMappedPossibleExemptions(instructions: any): ChargePossibleExemptions[] {
    return Object.keys(instructions).flatMap(key => {
      return instructions[key].map((i: any) => {
        return {
          possibleExemptionId: Math.random().toString(),
          fuelTypeLimitation: i.fuelTypeLimitation,
          possibleExemptionReason: i.fuelTypeLimitation?.limitationReason,
          fuelTypeLable: this.translateFuelLabel(i.fuelTypeLimitation?.allowedFuelTypeId),
          vehicles: i.vehicles,
        };
      });
    });
  }

  @action.bound translate(code: string) {
    return this.localizationService.translateGeneral(`multiEshop.${code}`);
  }

  @action.bound formatPrice(price: number) {
    return this.localizationService.formatPrice(price);
  }

  @action.bound translateFuelLabel(fuelTypeId: string) {
    const fuelTypes = this.enumService.values("fuel_types") as FuelTypeResponse[];
    const fuelType = fuelTypes.find(t => t.id.toString() === fuelTypeId)?.fuelType;
    if (!fuelType) {
      return false;
    }
    return this.localizationService.translateAttribute("fuelType", fuelType);
  }

  @action.bound
  public onContinue() {
    this.chargeOrderService.persistChargeOrder();
    this.router.navigate("contact");
  }

  @action.bound
  public onBack() {
    this.router.navigate(BatchOrderViewModel);
  }

  @action.bound
  hideOverlayRecommendation = () => {
    this.overlayRecommendationHidden = true;
  };

  @action.bound
  hideExemptionOverlayRecommendation = () => {
    this.overlayExemptionRecommendationHidden = true;
  };

  @action.bound
  showDetail(item?: ChargeOrderItem, index = -1) {
    this.detail = item;
    if (this.detailIndex === index) {
      this.detailIndex = -1;
    } else {
      this.detailIndex = index;
    }
    return false;
  }

  @action.bound
  selectAllOverlays(idOverlay: string) {
    this.overlays.forEach(overlay => (overlay.selected = !overlay.selected));
  }

  @action.bound
  select = () => {
    this.allOverlaysSelected = this.overlays.reduce(
      (summary: boolean, overlay: ChargeOverlay) => summary && (overlay.selected || false),
      true
    );

    if (
      !this.allOverlaysSelected &&
      this.overlays.reduce((summary: boolean, overlay: ChargeOverlay) => summary || overlay.selected || false, false)
    ) {
      this.allOverlaysSelected = null;
    }
  };

  @action.bound
  hideExemptionRecommendation = (id?: string) => {
    if (!id) return;
    this.exemptions = this.exemptions.filter(exemption => exemption.possibleExemptionId !== id);
  };

  @action.bound
  hideIdentifiedPossibleRecommendation = (id?: string) => {
    if (!id) return;
    this.exemptionsIdentifiedPossible = this.exemptionsIdentifiedPossible.filter(
      exemption => exemption.possibleExemptionId !== id
    );
  };

  @action.bound
  removeBatch = async (charge: ChargeOrderItem) => {
    const licensePlates = charge.licensePlates?.map(vehicle => vehicle);
    if (!licensePlates) return;
    this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges
      ?.map(orderItem => {
        orderItem.licensePlates = orderItem.licensePlates.filter(licensePlate => !licensePlates.includes(licensePlate));
        return orderItem;
      })
      .filter(item => item.licensePlates && item.licensePlates.length !== 0);

    this.overlays = this.overlays?.filter(overlay => !licensePlates.includes(overlay.licensePlate));
    this.overlapsExemption = this.overlapsExemption?.filter(overlay => !licensePlates.includes(overlay.vehicle.licensePlate));
    this.exemptions = this.exemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !licensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.possibleExemptions = this.possibleExemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !licensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);

    this.chargeOrderService.persistChargeOrder();

    await this.goBackIfNeeded();
  };

  @action.bound
  removeExemptionsFromOrderItems = async (exemption: ChargeExemptions) => {
    const licensePlates = exemption.vehicles?.map(vehicle => vehicle.licensePlate);
    if (!licensePlates) return;
    this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges
      ?.map(orderItem => {
        orderItem.licensePlates = orderItem.licensePlates.filter(licensePlate => !licensePlates.includes(licensePlate));
        return orderItem;
      })
      .filter(item => item.licensePlates && item.licensePlates.length !== 0);

    this.overlays = this.overlays?.filter(overlay => !licensePlates.includes(overlay.licensePlate));
    this.overlapsExemption = this.overlapsExemption?.filter(overlay => !licensePlates.includes(overlay.vehicle.licensePlate));
    this.exemptions = this.exemptions?.filter(ex => ex.possibleExemptionId !== exemption.possibleExemptionId);
    this.exemptions = this.exemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !licensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0)
      .filter(ex => ex.possibleExemptionId !== exemption.possibleExemptionId);
    this.exemptionsIdentifiedPossible = this.exemptionsIdentifiedPossible
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !licensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.chargeOrderService.persistChargeOrder();

    await this.goBackIfNeeded();
  };

  @action.bound
  removeIdentifiedPossibleFromOrderItems = async (identifiedPossible: ChargeExemptions) => {
    const licensePlates = identifiedPossible.vehicles?.map(vehicle => vehicle.licensePlate);
    if (!licensePlates) return;
    this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges
      ?.map(orderItem => {
        orderItem.licensePlates = orderItem.licensePlates.filter(licensePlate => !licensePlates.includes(licensePlate));
        return orderItem;
      })
      .filter(item => item.licensePlates && item.licensePlates.length !== 0);

    this.overlays = this.overlays?.filter(overlay => !licensePlates.includes(overlay.licensePlate));
    this.overlapsExemption = this.overlapsExemption?.filter(overlay => !licensePlates.includes(overlay.vehicle.licensePlate));

    this.exemptions = this.exemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !licensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);

    this.exemptionsIdentifiedPossible = this.exemptionsIdentifiedPossible?.filter(
      ex => ex.possibleExemptionId !== identifiedPossible.possibleExemptionId
    );

    this.chargeOrderService.persistChargeOrder();

    await this.goBackIfNeeded();
  };

  @action.bound
  removeOverlaysFromOrderItems = async () => {
    this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges
      .map(orderItem => {
        orderItem.licensePlates = orderItem.licensePlates.filter(
          licensePlate =>
            !toJS(this.overlayLicensePlates.filter(overlay => overlay.selected))
              .flatMap(overlay => overlay.licensePlate)
              .includes(licensePlate)
        );
        return orderItem;
      })
      .filter(item => item.licensePlates && item.licensePlates.length !== 0);

    const selectedLicensePlates = this.overlays?.filter(o => o.selected).map(s => s.licensePlate);
    this.overlays = this.overlays?.filter(overlay => !selectedLicensePlates.includes(overlay.licensePlate));
    this.overlapsExemption = this.overlapsExemption?.filter(
      overlay => !selectedLicensePlates.includes(overlay.vehicle.licensePlate)
    );
    this.exemptions = this.exemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !selectedLicensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.exemptionsIdentifiedPossible = this.exemptionsIdentifiedPossible
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !selectedLicensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.chargeOrderService.persistChargeOrder();

    await this.goBackIfNeeded();
  };

  @action.bound
  removeExemptionOverlaysFromOrderItems = async () => {
    this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges
      .map(orderItem => {
        orderItem.licensePlates = orderItem.licensePlates.filter(
          licensePlate =>
            !toJS(this.overlapsExemption.filter(overlay => overlay.vehicle.licensePlate))
              .flatMap(overlay => overlay.vehicle.licensePlate)
              .includes(licensePlate)
        );
        return orderItem;
      })
      .filter(item => item.licensePlates && item.licensePlates.length !== 0);

    const selectedLicensePlates = this.overlapsExemption?.map(s => s.vehicle.licensePlate);
    this.overlays = this.overlays?.filter(overlay => !selectedLicensePlates.includes(overlay.licensePlate));
    this.overlapsExemption = this.overlapsExemption?.filter(
      overlay => !selectedLicensePlates.includes(overlay.vehicle.licensePlate)
    );
    this.exemptions = this.exemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !selectedLicensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.exemptionsIdentifiedPossible = this.exemptionsIdentifiedPossible
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !selectedLicensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.chargeOrderService.persistChargeOrder();

    await this.goBackIfNeeded();
  };

  @action.bound
  exemptionReasonWarning(possibleExemptionReasonIds: string[]): string {
    if (!possibleExemptionReasonIds?.length) {
      return this.localizationService.translateGeneral("errors.generic");
    }
    const possibleExemptionReasonId = possibleExemptionReasonIds[0];
    const exemptionReasons = this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
    const matchingReason = exemptionReasons.find(x => x.id === possibleExemptionReasonId);
    switch (matchingReason?.exemptionReason) {
      case "C":
        return ChargeOrderInstructionType.IdentifiedPossibleExemption + "_C";
      case "P":
        return ChargeOrderInstructionType.IdentifiedPossibleExemption + "_P";
      case "O":
        return ChargeOrderInstructionType.IdentifiedPossibleExemption + "_O";
      case "O1":
        return ChargeOrderInstructionType.IdentifiedPossibleExemption + "_O";
      case "O2":
        return ChargeOrderInstructionType.IdentifiedPossibleExemption + "_O";
      default:
        return this.localizationService.translateGeneral("errors.generic");
    }
  }

  @action.bound
  groupByOverlapTypes(data: any) {
    return data.reduce((acc: any, obj: any) => {
      obj.overlapTypes.forEach((type: any) => {
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(obj);
      });
      return acc;
    }, {});
  }

  @action.bound
  groupByInstructionTypeType(data: any) {
    return data.reduce((acc: any, obj: any) => {
      const key = obj.instructionType;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
  }

  @action.bound
  groupByPossibleExemptionReason(data: any) {
    return data.reduce((acc: any, obj: any) => {
      const key = obj.possibleExemptionReason;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
  }

  @action.bound
  groupByCountry(data: any) {
    return data.reduce((acc: any, obj: any) => {
      const key = obj.countryId;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
  }

  @action.bound
  filterByInstructionType(groupedInstructions: any, isFuelTypeLimitation = false) {
    return Object.keys(groupedInstructions)
      .filter(instructionType =>
        isFuelTypeLimitation
          ? instructionType === ChargeOrderInstructionType.IdentifiedFuelTypeLimitation
          : instructionType !== ChargeOrderInstructionType.IdentifiedFuelTypeLimitation
      )
      .reduce((acc: any, instructionType) => {
        acc[instructionType] = groupedInstructions[instructionType];
        return acc;
      }, {});
  }

  @action.bound
  filterIdentifiedPossible(groupedInstructions: any, isIdentifiedPossible = false) {
    return Object.keys(groupedInstructions)
      .filter(instructionType =>
        isIdentifiedPossible
          ? instructionType === ChargeOrderInstructionType.IdentifiedPossibleExemption
          : instructionType !== ChargeOrderInstructionType.IdentifiedPossibleExemption
      )
      .reduce((acc: any, instructionType) => {
        acc[instructionType] = groupedInstructions[instructionType];
        return acc;
      }, {});
  }

  @action.bound
  protected vehicleChargeOrderInstructions(data: {
    type: string;
    payload: { chargeOrderInstructionGroups?: ChargeOrderInstructionGroupsResponse };
  }) {
    const instructions = data.payload.chargeOrderInstructionGroups?.items;
    if (!instructions) return;
    const groupedInstructions: any = this.groupByInstructionTypeType(instructions);
    const exemptions = this.filterIdentifiedPossible(this.filterByInstructionType(groupedInstructions));
    const exemptionsIdentifiedPossible = this.filterIdentifiedPossible(this.filterByInstructionType(groupedInstructions), true);

    const possibleExemptions = this.filterByInstructionType(groupedInstructions, true);
    const exemptionsMapped: ChargeExemptions[] = this.getMappedExemptions(exemptions);
    const exemptionsMappedIdentifiedPossible: ChargeExemptions[] = this.getMappedExemptions(exemptionsIdentifiedPossible);
    const possibleExemptionsMapped: ChargePossibleExemptions[] = this.getMappedPossibleExemptions(possibleExemptions);

    if (exemptionsMapped) {
      this.exemptions = Array.from(new Set([...this.exemptions, ...exemptionsMapped]));
      this.exemptions = this.exemptions.sort((a, b) => {
        return (
          ChargeVehicleWarningReasonOrder[a.instructionType as keyof ChargeVehicleWarningReasonOrder] -
          ChargeVehicleWarningReasonOrder[b.instructionType as keyof ChargeVehicleWarningReasonOrder]
        );
      });
    }
    if (exemptionsMappedIdentifiedPossible) {
      this.exemptionsIdentifiedPossible = Array.from(
        new Set([
          ...this.exemptionsIdentifiedPossible,
          ...this.getMappedExemptions(this.groupByPossibleExemptionReason(exemptionsMappedIdentifiedPossible)),
        ])
      );
      this.exemptionsIdentifiedPossible = this.exemptionsIdentifiedPossible.sort((a, b) => {
        return (
          ChargeVehicleWarningReasonOrder[a.instructionType as keyof ChargeVehicleWarningReasonOrder] -
          ChargeVehicleWarningReasonOrder[b.instructionType as keyof ChargeVehicleWarningReasonOrder]
        );
      });
    }
    if (possibleExemptionsMapped) {
      this.possibleExemptions = Array.from(new Set([...this.possibleExemptions, ...possibleExemptionsMapped]));
      this.possibleExemptions = this.possibleExemptions.sort((a, b) => {
        return (
          FuelTypeLimitationReasonOrder[a.fuelTypeLimitation?.limitationReason as keyof FuelTypeLimitationReasonOrder] -
          FuelTypeLimitationReasonOrder[a.fuelTypeLimitation?.limitationReason as keyof FuelTypeLimitationReasonOrder]
        );
      });

      for (const exemption of possibleExemptionsMapped) {
        const groupedByCountry = this.groupByCountry(exemption.vehicles) as CountryData[];
        for (const key in groupedByCountry) {
          const vehicles = groupedByCountry[key];
          for (const vehicle of vehicles as any) {
            const originalCharges = this.chargeOrderCharges.filter(
              storedCharge =>
                vehicle.licensePlate &&
                storedCharge.licensePlates.includes(vehicle.licensePlate) &&
                vehicle.countryId === storedCharge.countryId
            );
            if (!originalCharges.length) return;
            for (const originalCharge of originalCharges) {
              const existedCharge = this.chargeOrderCharges.find(
                storedCharge =>
                  vehicle.countryId === storedCharge.countryId &&
                  storedCharge.fuelTypeId === exemption.fuelTypeLimitation?.allowedFuelTypeId &&
                  originalCharge.validSince === storedCharge.validSince &&
                  originalCharge.chargeTypeId === storedCharge.chargeTypeId
              );

              if (existedCharge && originalCharge) {
                originalCharge.licensePlates = originalCharge.licensePlates?.filter(l => l !== vehicle.licensePlate);
                existedCharge.licensePlates.push(vehicle.licensePlate);
                this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges.filter(
                  item => item.licensePlates && item.licensePlates.length !== 0
                );
                this.chargeOrderService.persistStoredChargeOrder(this.chargeOrderService.chargeOrder);
                continue;
              } else {
                originalCharge.licensePlates = originalCharge.licensePlates?.filter(l => l !== vehicle.licensePlate);
                const chargeOrderItem = this.chargeOrderService.addChargeOrderItem();
                chargeOrderItem.licensePlates.push(vehicle.licensePlate);
                chargeOrderItem.validity.hasImmediateValidity = chargeOrderItem._hasImmediateValidity =
                  originalCharge._hasImmediateValidity;
                chargeOrderItem.validity.validSinceDate = chargeOrderItem.validSince = originalCharge.validSince;
                chargeOrderItem.validity._validUntilDate = chargeOrderItem._validUntil = originalCharge._validUntil;
                chargeOrderItem.chargeTypeId = chargeOrderItem._chargeTypeId = originalCharge.chargeTypeId;
                chargeOrderItem.countryId = originalCharge.countryId;
                if (exemption.fuelTypeLimitation?.allowedFuelTypeId) {
                  chargeOrderItem.fuelTypeId = chargeOrderItem._fuelTypeId = exemption.fuelTypeLimitation?.allowedFuelTypeId;
                }
                chargeOrderItem._priceListItems = this.chargeTypesService.chargePriceList?.items.filter(
                  item => item.fuelTypeId === chargeOrderItem.fuelTypeId
                );
                chargeOrderItem.priceListItemId =
                  chargeOrderItem._priceListItems.find(
                    item =>
                      item.fuelTypeId === exemption.fuelTypeLimitation?.allowedFuelTypeId &&
                      item.chargeTypeId === chargeOrderItem.chargeTypeId
                  )?.id ?? "";
                chargeOrderItem._allowedDelete = true;
                this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges.filter(
                  item => item.licensePlates && item.licensePlates.length !== 0
                );
                this.chargeOrderService.persistStoredChargeOrder(this.chargeOrderService.chargeOrder);
              }
            }
          }
        }
      }
    }
  }
  @action.bound
  protected async vehicleChargeValidityInstructions(data: {
    type: string;
    payload: { chargeValidityInstructionGroups?: ChargeValidityInstructionGroupsResponse };
  }) {
    const response = data.payload.chargeValidityInstructionGroups;
    if (response?.validities) {
      await this.setChargeValidityByInstructions(response.validities);
    }

    if (response?.overlaps) {
      const groupedOverlaps = this.groupByOverlapTypes(response?.overlaps);
      const overlapsCharge = groupedOverlaps[ChargeOverlapType.ChargeOverlap] as ChargeValidityOverlapInstructionsGroupResponse[];
      this.overlapsExemption = groupedOverlaps[
        ChargeOverlapType.ExemptionOverlap
      ] as ChargeValidityOverlapInstructionsGroupResponse[];

      let overlays: ChargeOverlay[] = [];

      overlapsCharge?.forEach(o => {
        const charge = this.chargeOrderService.chargeOrder.charges.find(
          charge =>
            charge.priceListItemId === o.priceListItemId &&
            ((charge._hasImmediateValidity && o.requestedValidity.hasImmediateValidity) ||
              (!charge._hasImmediateValidity &&
                !o.requestedValidity.hasImmediateValidity &&
                o.requestedValidity.validSinceDate &&
                charge.validSince &&
                isSamePragueDay(new Date(o.requestedValidity.validSinceDate), charge.validSince)))
        );

        if (charge && o.overlappingCharges) {
          this.chargeOrderService.persistChargeOrder();
          const overlapPeriod = o.overlappingCharges.map(overlapPeriod => {
            return {
              licensePlate: o.vehicle.licensePlate ?? "",
              state: OverlayValidity.VALID,
              selected: false,
              expiration: overlapPeriod.validUntil,
            };
          });
          overlays = [...overlays, ...overlapPeriod];
        }
      });
      this.overlays = [...this.overlays, ...overlays];
    }
  }

  @action.bound
  protected async setChargeValidityByInstructions(validities: ChargeValidityInstructionGroupResponse[]) {
    const updated = await Promise.all(
      this.chargeOrderService.chargeOrderItems.map(async charge => {
        const match = validities.find(
          v =>
            charge.priceListItemId === v.priceListItemId &&
            ((charge._hasImmediateValidity && v.requestedValidity.hasImmediateValidity) ||
              (charge.validSince && dateToIsoDate(charge.validSince) === dateToIsoDate(v.validSince)))
        );
        if (match) {
          const nowOrSince = charge._hasImmediateValidity ? await this.timeService.getCurrentServerTime(true) : match.validSince;

          return {
            ...charge,
            validSince: nowOrSince,
            validity: {
              ...charge.validity,
              validSinceDate: nowOrSince,
              _validUntilDate: endOfCzechDay(match.validUntil),
              _validUntilOverlap: endOfCzechDay(match.validUntil),
            },
            _validUntil: endOfCzechDay(match.validUntil),
            _validUntilOverlap: endOfCzechDay(match.validUntil),
          };
        }
        return charge;
      })
    );

    runInAction(() => {
      this.chargeOrderService.chargeOrder.charges.splice(
        0,
        this.chargeOrderService.chargeOrder.charges.length,
        ...updated.map(data => Object.assign(new ChargeOrderItem(), data))
      );
    });

    this.chargeOrderService.persistChargeOrder();
  }

  @action.bound
  resetChargeOrder() {
    this.chargeOrderService.reset();
    this.router.navigate(BatchOrderViewModel);
  }

  @action.bound
  loadVehicleInformation = async () => {
    const isChargesModified = this.chargeOrderService.chargeOrder.charges.some(charge => charge._isModified === true);
    if (this.chargeOrderService.chargeOrder.charges.length && isChargesModified) {
      try {
        this.loading = true;
        await this.vehicleService.getChargeOrderInstructions(this.chargeOrderService.chargeOrder.charges);
        await this.vehicleService.getChargeValidityInstructions(this.chargeOrderService.chargeOrder.charges);
        this.chargeOrderService.chargeOrder.charges.forEach(charge => (charge._isModified = false));
      } catch (e) {
        // Silence catch of background process
      } finally {
        this.loading = false;
      }
    } else {
      this.loading = false;
    }
  };

  @action.bound
  toggleChargeOrderItem = (chargeOrderItem: ChargeOrderItem) => () => (chargeOrderItem._visible = !chargeOrderItem._visible);

  @action.bound
  chargePrice(item: ChargeOrderItem) {
    return this.chargeOrderService.chargePrice(item.chargeTypeId as string, item.fuelTypeId as string);
  }

  @action.bound
  formatValidFrom(item: ChargeOrderItem) {
    return item.validSince ? this.localizationService.formatDateTime(new Date(item.validSince)) : undefined;
  }

  @action.bound
  formatValidTo(item: ChargeOrderItem) {
    return item._validUntil ? this.localizationService.formatDateTime(new Date(item._validUntil)) : undefined;
  }

  @action.bound
  formatExpiration(date: Date) {
    return this.localizationService.formatDateTime(new Date(date));
  }

  @action.bound
  formatOverlayState(state: OverlayValidity) {
    return this.translate(`overlay.state.${state}`);
  }

  @action.bound
  getCountry(countryId: string) {
    const country = this.enumService.values("countries").find((country: Enum) => country.id === countryId);
    return !country ? "-" : country.name;
  }
}
